import React from 'react';
import { useTranslation } from 'react-i18next';

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted, bold }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: bold ? 700 : muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const fmtWt = (n) => (n != null ? `${parseFloat(n).toFixed(2)}` : '—');

const ChallanProductsTable = ({ items = [], loading, groupBy = 'parent' }) => {
    const { t } = useTranslation();
    const isSkuMode = groupBy === 'sku';

    return (
        <div style={cardStyle}>
            {/* Card header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('challanAnalytics.tables.productBreakdown')}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                        {t('challanAnalytics.labels.groupedByPrefix')}{isSkuMode ? t('challanAnalytics.labels.productSizeSku') : t('challanAnalytics.labels.productCatalogue')}
                    </div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#189AB4', backgroundColor: '#ecfeff', borderRadius: 20, padding: '3px 10px' }}>
                        {items.length} {t('challanAnalytics.labels.productsCountSuffix')}
                    </span>
                )}
            </div>

            {loading && <EmptyState message={t('challanAnalytics.emptyStates.loadingProducts')} />}
            {!loading && !items.length && <EmptyState message={t('challanAnalytics.emptyStates.noProductData')} />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                        <thead>
                            <tr>
                                <Th>#</Th>
                                <Th>{t('common.analyticsColumns.productName')}</Th>
                                {isSkuMode && <Th>{t('challanAnalytics.columns.skuSize')}</Th>}
                                <Th align="right">{t('challanAnalytics.columns.qtyUnits')}</Th>
                                <Th align="right">{t('challanAnalytics.columns.weightT')}</Th>
                                <Th align="right">{t('common.analyticsColumns.challans')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => (
                                <tr
                                    key={row.id || row.product_id || row.sku_id || i}
                                    style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                >
                                    <Cell muted>{i + 1}</Cell>
                                    <Cell bold>
                                        {row.product_name ?? row.catalogue_name ?? row.name ?? '—'}
                                    </Cell>
                                    {isSkuMode && (
                                        <Cell muted>{row.sku ?? row.size ?? row.product_size ?? '—'}</Cell>
                                    )}
                                    <Cell align="right">
                                        {row.total_quantity != null
                                            ? `${fmt(row.total_quantity)} ${row.unit ?? ''}`
                                            : '—'}
                                    </Cell>
                                    <Cell align="right">
                                        {fmtWt(row.total_weight_tons ?? row.weight_tons ?? row.weight)}
                                    </Cell>
                                    <Cell align="right" muted>
                                        {fmt(row.challan_count ?? row.count)}
                                    </Cell>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default ChallanProductsTable;
